<template>
  <div class="badge-container">
    <span :class="['status-badge', getStatusClass(status)]">
      {{ getStatusLabel(status) }}
      <span 
        v-if="!hideInfoIcon && (showIcon || computedTooltipText)" 
        class="icon-container" 
        @mouseenter="showTooltip = true" 
        @mouseleave="showTooltip = false" 
        ref="iconRef"
        :style="{ '--svg-color': getComputedColor() }"
      >
        <SvgIcon name="info" class="info-icon" />
      </span>
    </span>
    
    <InfoTagPopup
      v-if="!hideInfoIcon && showTooltip && (showIcon || computedTooltipText)"
      :message="computedTooltipText"
      :position="tooltipPosition"
      :triggerRef="$refs.iconRef"
      @close="showTooltip = false"
    />
  </div>
</template>


<script>
import SvgIcon from '@/components/ui/svg/SvgIcon.vue';
import InfoTagPopup from '@/components/ui/dataTable/InfoTagPopup.vue';

export default {
  name: "StatusCell",
  components: {
    SvgIcon,
    InfoTagPopup
  },
  
  props: {
    status: {
      type: String,
      required: true
    },
    showIcon: {
      type: Boolean,
      default: false
    },
    tooltipText: {
      type: String,
      default: ""
    },
    tooltipPosition: {
      type: String,
      default: "top"
    },
    hideInfoIcon: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      showTooltip: false
    }
  },
  
  computed: {
    computedTooltipText() {
      return this.tooltipText || this.getDefaultTooltip(this.status);
    }
  },
  
  methods: {
    getStatusLabel(status) {
      // Cette méthode peut soit utiliser la valeur directement soit traduire via des clés
      // Si le status est un code interne d'article, on le traduit
      if (status.startsWith('to_') || ['on_sale', 'unpublished'].includes(status)) {
        const statusMap = {
          to_publish: "article_put_online.tabs.to_publish",
          on_sale: "article_put_online.tabs.on_sale",
          to_correct: "article_put_online.tabs.to_correct",
          draft: "article_put_online.tabs.draft",
          unpublished: "article_put_online.tabs.unpublished"
        };
        return this.$t(statusMap[status] || status);
      }
      
      // Si c'est un statut de collection technique, on le traduit
      const collectionStatusTranslations = {
        "completed": this.$t('collections.status.completed'),
        "draft": this.$t('collections.status.draft')
      };
      
      if (status in collectionStatusTranslations) {
        return collectionStatusTranslations[status];
      }
      
      // Sinon, on l'utilise comme tel (déjà traduit)
      return status;
    },
    
    getStatusClass(status) {
      // Pour les codes internes d'articles
      const articleStatusMap = {
        to_publish: "status-badge--to-publish",
        on_sale: "status-badge--on-sale",
        to_correct: "status-badge--to-correct",
        draft: "status-badge--draft",
        unpublished: "status-badge--unpublished"
      };
      
      // Pour les statuts de collections (nouvelles valeurs techniques + anciennes valeurs françaises)
      const collectionStatusMap = {
        // Nouvelles valeurs techniques
        "completed": "status-badge--complete",
        "draft": "status-badge--draft",
        // Anciennes valeurs françaises (pour compatibilité)
        "Complète": "status-badge--complete",
        "Brouillon": "status-badge--draft",
        // Valeurs traduites en anglais
        "Completed": "status-badge--complete",
        "Draft": "status-badge--draft"
      };
      
      // Vérifie d'abord dans la carte des statuts d'articles
      if (status in articleStatusMap) {
        return articleStatusMap[status];
      }
      
      // Sinon, vérifie dans la carte des statuts de collections
      return collectionStatusMap[status] || "";
    },
    
    getDefaultTooltip(status) {
      const tooltips = {
        // Nouvelles valeurs techniques
        "completed": this.$t("collections.status.completed_tooltip"),
        "draft": this.$t("collections.status.draft_tooltip"),
        // Anciennes valeurs françaises (pour compatibilité)
        "Complète": this.$t("collections.status.completed_tooltip"),
        "Brouillon": this.$t("collections.status.draft_tooltip"),
        // Valeurs traduites en anglais
        "Completed": this.$t("collections.status.completed_tooltip"),
        "Complete": this.$t("collections.status.completed_tooltip"),
        "Draft": this.$t("collections.status.draft_tooltip")
      };
      
      return tooltips[status] || "";
    },
    toggleTooltip() {
      this.showTooltip = !this.showTooltip;
    },
    getComputedColor() {
      return 'currentColor';
    }
  }
};
</script>

<style scoped>
.badge-container {
  position: relative;
  display: inline-block;
}

.status-badge {
  position: relative  ;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  border: 1px solid;
}

.icon-container {
  display: inline-flex;
  margin-left: 6px;
  cursor: help;
  --svg-color: currentColor;
}

.info-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  min-width: 14px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3);
  font-size: 10px;
}

/* Styles pour les statuts d'articles */
.status-badge--to-publish {
  background: #fff4ed;
  color: #fea36f;
  border-color: #fea36f;
}

.status-badge--on-sale {
  background: #f1f1fe;
  color: #6f71e6;
  border-color: #6f71e6;
}

.status-badge--to-correct {
  background: #ffeeed;
  color: #fe3737;
  border-color: #fe3737;
}

.status-badge--draft {
  background: #fff;
  color: #000;
  border-color: #e0e0e0;
}

.status-badge--unpublished {
  background: #f7f7f7;
  color: #111;
  border-color: #d0d0d0;
}

/* Styles pour les statuts de collections */
.status-badge--complete {
  background-color: #E6FDF4;
  color: #18A47A;
  border-color: #18A47A;
}
</style>